import { useEffect, useRef, useState } from "react";
import { AlertTriangle, BellRing, CheckCircle2, Circle, RefreshCw, Send, Undo2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DocumentsPanel from "@/components/DocumentsPanel";
import { fmtDate } from "@/lib/utils";

// 2026-10-27 client review: a Ramp charge is coded right here — category
// (QuickBooks account via the cost code) + job, memo, receipt — then an
// admin flags what's missing and marks it ready. Ramp posts it to QuickBooks
// in its next sync; ScapeSource keeps the job cost and writes the coding
// back to Ramp. Every step that touches Ramp reports its own result.

const ITEM_LABELS = { category: "QuickBooks category", job: "Job", memo: "Memo", receipt: "Receipt" };
const STEP_LABELS = { coding: "Category & job", memo: "Memo", receipt: "Receipt", flag: "Cardholder notice", ready: "Mark ready" };
const ctxKey = (c) => (!c ? "" : c.type === "non_job" ? `ctx:${c.context_id}` : `job:${c.job_id}`);

const errText = (e, fallback) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.message || x.msg).join(" ") : d || fallback;
};

export default function RampCodingPanel({ txn, canManage, onChange }) {
  const [jobs, setJobs] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [codes, setCodes] = useState([]);
  const line = txn.assignment?.lines?.[0];
  const [ctx, setCtx] = useState(ctxKey(line?.context));
  const [costCodeId, setCostCodeId] = useState(line?.cost_code_id || "");
  const [memo, setMemo] = useState(txn.memo || "");
  const [busy, setBusy] = useState("");
  const [flagNote, setFlagNote] = useState("");
  const [showFlag, setShowFlag] = useState(false);
  const [confirmAnyway, setConfirmAnyway] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data.filter((j) => j.status === "active"))).catch(() => {});
    api.get("/contractor/contexts").then((r) => setContexts(r.data.filter((c) => c.active !== false))).catch(() => {});
    api.get("/contractor/cost-codes").then((r) => setCodes(r.data.filter((c) => c.active !== false))).catch(() => {});
  }, []);

  // A fresh version of the charge (after any step) refills the form.
  useEffect(() => {
    const l = txn.assignment?.lines?.[0];
    setCtx(ctxKey(l?.context)); setCostCodeId(l?.cost_code_id || ""); setMemo(txn.memo || "");
  }, [txn.id, txn.status, txn.memo, txn.ramp_ready_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const coded = txn.status === "coded";
  const locked = !!txn.ramp_ready_at;
  const missing = txn.missing_items || [];
  const push = txn.ramp_push || {};

  const run = async (name, fn, success) => {
    setBusy(name);
    try {
      const { data } = await fn();
      onChange(data);
      if (success) toast.success(success);
      return true;
    } catch (e) {
      toast.error(errText(e, "That didn't work"));
      return false;
    } finally {
      setBusy("");
    }
  };

  const save = () => {
    if (!ctx) return toast.error("Choose the job (or purchasing context)");
    if (!costCodeId) return toast.error("Choose the cost code — it sets the QuickBooks category");
    const context = ctx.startsWith("job:") ? { type: "job", job_id: ctx.slice(4) } : { type: "non_job", context_id: ctx.slice(4) };
    return run("code", () => api.post(`/contractor/card-transactions/${txn.id}/code`, { context, cost_code_id: costCodeId, memo: memo.trim() || null }),
      coded ? "Coding updated" : "Charge coded — it now counts toward the job");
  };

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    await run("receipt", () => api.post(`/contractor/card-transactions/${txn.id}/receipt`, form, { headers: { "Content-Type": "multipart/form-data" } }), "Receipt attached");
    if (fileRef.current) fileRef.current.value = "";
  };

  const markReady = (force) => run("ready", () => api.post(`/contractor/card-transactions/${txn.id}/ready`, { force }), "Marked ready — Ramp will post it in its next QuickBooks sync")
    .then((ok) => ok && setConfirmAnyway(false));

  const selectCls = "w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px] bg-white disabled:bg-slate-50 disabled:text-slate-500";

  return (
    <div className="mt-5 rounded-xl border border-sky-100 bg-sky-50/40 px-4 py-4" data-testid="ramp-coding-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-sky-700 font-bold">Code this charge</div>
        {coded && !locked && (
          <button disabled={!!busy} onClick={() => run("uncode", () => api.post(`/contractor/card-transactions/${txn.id}/uncode`), "Back to unmatched")}
            data-testid="uncode-button" className="text-[12px] text-slate-500 hover:text-slate-800 flex items-center gap-1"><Undo2 size={12} /> Uncode</button>
        )}
      </div>
      <p className="text-[12px] text-slate-500 mt-1">Ramp posts this charge to QuickBooks. Code it here — no Expense Log entry needed.</p>

      <div className="mt-3 space-y-2.5">
        <div>
          <label className="text-[11.5px] font-semibold text-slate-600">Job</label>
          <select value={ctx} onChange={(e) => setCtx(e.target.value)} disabled={locked} data-testid="ramp-code-job" className={selectCls}>
            <option value="">— choose a job —</option>
            <optgroup label="Jobs">{jobs.map((j) => <option key={j.id} value={`job:${j.id}`}>{j.code} — {j.name}</option>)}</optgroup>
            {contexts.length > 0 && <optgroup label="Not a job">{contexts.map((c) => <option key={c.id} value={`ctx:${c.id}`}>{c.name}</option>)}</optgroup>}
          </select>
        </div>
        <div>
          <label className="text-[11.5px] font-semibold text-slate-600">Cost code (QuickBooks category)</label>
          <select value={costCodeId} onChange={(e) => setCostCodeId(e.target.value)} disabled={locked} data-testid="ramp-code-costcode" className={selectCls}>
            <option value="">— choose a cost code —</option>
            {codes.map((c) => <option key={c.id} value={c.id}>{c.code} {c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11.5px] font-semibold text-slate-600">Memo</label>
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} disabled={locked} rows={2} data-testid="ramp-code-memo"
            placeholder="What was this for?" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12.5px] disabled:bg-slate-50" />
        </div>
        {!locked && (
          <button disabled={!!busy} onClick={save} data-testid="ramp-code-save"
            className="h-9 px-4 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold disabled:opacity-60">
            {busy === "code" ? "Saving…" : coded ? "Update coding" : "Code charge"}
          </button>
        )}
      </div>

      {coded && (
        <>
          <div className="mt-4 border-t border-sky-100 pt-3" data-testid="ramp-checklist">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1.5">Before it can be marked ready</div>
            <div className="grid grid-cols-2 gap-1.5 text-[12.5px]">
              {Object.keys(ITEM_LABELS).map((k) => (
                <div key={k} className={`flex items-center gap-1.5 ${missing.includes(k) ? "text-amber-700" : "text-emerald-700"}`} data-testid={`check-${k}`}>
                  {missing.includes(k) ? <Circle size={13} /> : <CheckCircle2 size={13} />} {ITEM_LABELS[k]}
                </div>
              ))}
            </div>
            {txn.missing_info && (
              <div className="mt-2.5 text-[12px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="missing-info-flag">
                <span className="font-semibold">{txn.missing_info.flagged_by_name} asked for: </span>
                {(txn.missing_info.items || []).map((i) => ITEM_LABELS[i]).join(", ")}{txn.missing_info.message ? ` — ${txn.missing_info.message}` : ""}
              </div>
            )}
          </div>

          {!locked && (
            <div className="mt-3">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="hidden" data-testid="ramp-receipt-input" />
              <button disabled={!!busy} onClick={() => fileRef.current?.click()} data-testid="ramp-receipt-button"
                className="h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60">
                <Upload size={13} /> {busy === "receipt" ? "Sending…" : "Add receipt"}
              </button>
            </div>
          )}
          <div className="mt-3"><DocumentsPanel entityType="imported_transaction" entityId={txn.id} title="Receipts" compact readOnly /></div>

          {Object.keys(STEP_LABELS).some((k) => push[k]) && (
            <div className="mt-3 border-t border-sky-100 pt-3 space-y-1" data-testid="ramp-push-status">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1">Sent to Ramp</div>
              {Object.keys(STEP_LABELS).filter((k) => push[k]).map((k) => (
                <div key={k} className={`text-[12px] flex gap-1.5 ${push[k].ok ? "text-slate-600" : "text-red-700"}`} data-testid={`push-${k}`}>
                  {push[k].ok ? <CheckCircle2 size={13} className="mt-0.5 text-emerald-600 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
                  <span><b>{STEP_LABELS[k]}</b>{push[k].ok ? "" : ` — ${push[k].error}`}</span>
                </div>
              ))}
              {(!push.coding?.ok || (txn.memo && push.memo && !push.memo.ok)) && !locked && (
                <button disabled={!!busy} onClick={() => run("push", () => api.post(`/contractor/card-transactions/${txn.id}/push`), "Sent to Ramp again")}
                  data-testid="ramp-push-retry" className="mt-1 h-8 px-3 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold hover:bg-slate-50 flex items-center gap-1.5">
                  <Send size={12} /> Send to Ramp again
                </button>
              )}
            </div>
          )}

          <div className="mt-4 border-t border-sky-100 pt-3" data-testid="ramp-ready-section">
            {locked ? (
              <div className="text-[12.5px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3.5 py-2.5" data-testid="ramp-ready-banner">
                <div className="font-semibold">Marked ready{txn.ramp_ready_by_name ? ` by ${txn.ramp_ready_by_name}` : ""} · {fmtDate(txn.ramp_ready_at)}</div>
                <div className="mt-0.5">{txn.ramp_sync_status === "SYNCED"
                  ? `Ramp posted it to QuickBooks${txn.ramp_synced_at ? ` on ${fmtDate(txn.ramp_synced_at)}` : ""}.`
                  : "Ramp posts it to QuickBooks in its next sync."}</div>
                <button disabled={!!busy} onClick={() => run("refresh", () => api.post(`/contractor/card-transactions/${txn.id}/refresh`), "Checked with Ramp")}
                  data-testid="ramp-refresh" className="mt-2 h-8 px-3 rounded-lg border border-emerald-200 bg-white text-[12px] font-semibold flex items-center gap-1.5">
                  <RefreshCw size={12} className={busy === "refresh" ? "animate-spin" : ""} /> Check Ramp's sync
                </button>
              </div>
            ) : canManage ? (
              <div className="flex flex-wrap items-center gap-2">
                <button disabled={!!busy || missing.length > 0} onClick={() => markReady(false)} data-testid="ramp-mark-ready"
                  title={missing.length ? `Still missing: ${missing.map((i) => ITEM_LABELS[i]).join(", ")}` : ""}
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  <CheckCircle2 size={13} /> {busy === "ready" ? "Marking…" : "Mark ready"}
                </button>
                {missing.length > 0 && (
                  <>
                    <button disabled={!!busy} onClick={() => setShowFlag((v) => !v)} data-testid="ramp-flag-button"
                      className="h-9 px-3.5 rounded-lg border border-amber-200 bg-white text-amber-800 text-[12.5px] font-semibold flex items-center gap-1.5">
                      <BellRing size={13} /> Ask cardholder
                    </button>
                    {!confirmAnyway
                      ? <button onClick={() => setConfirmAnyway(true)} data-testid="ramp-ready-anyway" className="text-[12px] text-slate-500 hover:text-slate-800 underline">Mark ready anyway</button>
                      : <button disabled={!!busy} onClick={() => markReady(true)} data-testid="ramp-ready-anyway-confirm"
                          className="h-9 px-3.5 rounded-lg bg-amber-500 text-white text-[12.5px] font-semibold">Yes — mark ready with items missing</button>}
                  </>
                )}
              </div>
            ) : (
              <div className="text-[12px] text-slate-500">When everything above is complete, an admin or accounting marks it ready for Ramp's QuickBooks sync.</div>
            )}
            {canManage && showFlag && missing.length > 0 && !locked && (
              <div className="mt-2.5 space-y-2">
                <textarea value={flagNote} onChange={(e) => setFlagNote(e.target.value)} rows={2} data-testid="ramp-flag-note"
                  placeholder={`Asking for: ${missing.map((i) => ITEM_LABELS[i]).join(", ")} — add a note (optional)`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12.5px]" />
                <button disabled={!!busy} onClick={async () => {
                  if (await run("flag", () => api.post(`/contractor/card-transactions/${txn.id}/flag`, { message: flagNote.trim() || null }), "Cardholder notified")) {
                    setShowFlag(false); setFlagNote("");
                  }
                }} data-testid="ramp-flag-send" className="h-9 px-4 rounded-lg bg-amber-500 text-white text-[12.5px] font-semibold">Send to cardholder</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
